import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-adc-form',
  templateUrl: './adc-form.component.html',
  styleUrls: ['./adc-form.component.scss'],
  standalone: false
})
export class AdcFormComponent implements OnChanges{
  @Input() data: any;
  @Input() formData: any;
  @Input() adcPrimaryIptStyle: any;
  @Output() formValue: EventEmitter<any> = new EventEmitter();
  dynamicForm!: FormGroup;
  formValues: any;

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.initForm();
    this.patchValues(this.formValues);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes && changes['formData'].currentValue) {
      this.formValues = changes['formData'].currentValue;
      this.patchValues(this.formValues);
    }
  }

  initForm() {
    const formGroupConfig: { [key: string]: any } = {};
  
    this.data.formElements.forEach((row: any) => {
      row.elements.forEach((element: any) => {
        const validators: ValidatorFn[] = this.getValidators(element.validators);
        const initialValue = element.value !== undefined ? element.value : '';
        formGroupConfig[element.name] = [initialValue, validators];
      });
    });
  
    this.dynamicForm = this.formBuilder.group(formGroupConfig);
  
    this.dynamicForm.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.formValue.emit(value);
      });
  }
  

  getValidators(validatorsConfig: any[]): ValidatorFn[] {
    const validatorFns: ValidatorFn[] = [];
  
    if (validatorsConfig) {
      validatorsConfig.forEach(validator => {
        if (validator === Validators.required) {
          validatorFns.push(Validators.required);
        } else if (validator.email) {
          validatorFns.push(Validators.email);
        } else if (validator.minlength) {
          validatorFns.push(Validators.minLength(validator.minlength.requiredLength));
        } else if (validator.pattern) {
          validatorFns.push(Validators.pattern(validator.pattern));
        } else if (validator.zipcode) {
          validatorFns.push(this.zipCodeValidator());
        } else if (validator.phone) {
          validatorFns.push(Validators.pattern(/^\d{10}$/));
        } else if (validator.customEmailPattern) {
          validatorFns.push(Validators.pattern(validator.customEmailPattern));
        }

        validatorFns.push(this.whitespaceValidator());
      });
    }
    
    return validatorFns;
  }
  

  patchValues(values: { [key: string]: any }) {
    if (values) {
      this.dynamicForm.patchValue(values);
    }
  }

  submitForm() {
    if (this.dynamicForm.valid) {
      this.formValue.emit(this.dynamicForm.value);
    } else {
      this.dynamicForm.markAllAsTouched(); 
    }
  }

  getFormValues() {
    if (this.dynamicForm.valid) {
      return this.dynamicForm.value;
    } else {
      this.dynamicForm.markAllAsTouched(); 
      return null;
    }
  }

  zipCodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null; // If the field is empty, consider it as valid for now (can add required validation separately)
      }

      const length = value.length;
      if (!/^\d+$/.test(value)) {
        return { invalidFormat: true }; // Error for non-numeric characters
      }  else if (length < 5) {
        return { minZip: true }; // Error for too short ZIP code
      } else if (length > 10) {
        return { maxZip: true }; // Error for too long ZIP code
      }

      return null; // No errors
    };
  }

  whitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && value.trim().length === 0) {
        return { whitespace: true }; // Return error if value is only whitespace
      }
      return null; // No error
    };
  }

  isRequiredValidator(validators: any[]): boolean {
    return validators ? validators.some(validator => validator === Validators.required) : false;
  }

}
